import React from 'react';
import { useSelector } from 'react-redux';
import { find } from 'lodash';

import { Stack, ProgressBar } from 'react-bootstrap';
import {
  FaComment,
  FaRegComment,
  FaFlag,
  FaRegFlag,
} from 'react-icons/fa';
import styled from '@emotion/styled';

import { FlexDivSpaceBetweenCenter } from 'resources/styles/display';
import colors from 'resources/styles/colors';

import useUser from 'hooks/useUser';
import useChat from 'hooks/useChat';

const CriteriaProgress = styled.div`
  width: 60px;
  margin-left: 10px;
  .progress {
    border: 1px solid ${colors.green};
  }
  .progress-bar {
    background-color: ${colors.green};
  }
`;

const CriteriaSection = styled(FlexDivSpaceBetweenCenter)<{ isCurrent: boolean }>`
  padding: 1rem;
  border-top: 1px solid ${colors.gray};
  cursor: pointer;
  background-color: ${({ isCurrent }) => (isCurrent ? colors.lightGray : 'white')};
  color: ${({ isCurrent }) => (isCurrent ? colors.green : 'black')};
  &:first-of-type {
    border-top: none;
  }
`;

interface Question {
  name: string;
  visible: boolean;
  getType: () => string;
  isEmpty: () => boolean;
}

interface Page {
  name: string;
  title: string;
  questions: Question[];
}

interface CriteriaTabProps {
  page: Page;
  onTabClick: (page: Page) => void;
  isCurrent: boolean;
}

const CriteriaTab: React.FC<CriteriaTabProps> = ({ page, onTabClick, isCurrent }) => {
  const { isAdmin } = useUser();
  const showFlags = !isAdmin;

  const flagData = useSelector((state: any) => state.applications.flags.data as {
    questionId: string
  }[]);
  const { commentCount } = useChat();

  const {
    total,
    answered,
    flagTotal,
    commentTotal,
  } = page.questions.reduce(
    (res, question) => {
      if (!question.visible) return res;

      const questionType = question.getType();
      if (questionType === 'html') return res;

      res.total += 1;

      if (!question.isEmpty()) res.answered += 1;

      if (showFlags) {
        if (!res.commentTotal) {
          const comments = find(commentCount, { questionId: question.name });
          if (comments) res.commentTotal += comments.count;
        }
        if (!res.flagTotal) {
          const flag = find(flagData, { questionId: question.name });
          if (flag) res.flagTotal += 1;
        }
      }
      return res;
    },
    {
      total: 0,
      answered: 0,
      flagTotal: 0,
      commentTotal: 0,
    },
  );

  const barNow = total === 0 ? 100 : answered;
  const barMax = total === 0 ? 100 : total;

  return (
    <CriteriaSection
      isCurrent={isCurrent}
      onClick={() => !isCurrent && onTabClick(page)}
    >
      <span>{page.title}</span>
      <FlexDivSpaceBetweenCenter>
        {showFlags && (
          <Stack direction="horizontal" gap={2} style={{ fontSize: '1.3rem' }}>
            {commentTotal ? <FaComment /> : <FaRegComment />}
            {flagTotal ? <FaFlag /> : <FaRegFlag />}
          </Stack>
        )}
        <CriteriaProgress>
          <ProgressBar now={barNow} min={0} max={barMax} />
        </CriteriaProgress>
      </FlexDivSpaceBetweenCenter>
    </CriteriaSection>
  );
};

export default CriteriaTab;
